/**
 * OpenAI SDK adapter for morph-warp-grep tool
 */

import type { ChatCompletionTool } from 'openai/resources/chat/completions';
import { executeToolCall, formatResult, WarpGrepClient, executeGitHubReadFile, formatGitHubReadFileResult } from './client.js';
import { WARP_GREP_DESCRIPTION, WARP_GREP_TOOL_NAME, GITHUB_SEARCH_TOOL_NAME, GITHUB_SEARCH_DESCRIPTION, GITHUB_SEARCH_INPUT_SCHEMA, GITHUB_READ_FILE_TOOL_NAME, GITHUB_READ_FILE_DESCRIPTION, GITHUB_READ_FILE_INPUT_SCHEMA } from './prompts.js';
import type { WarpGrepToolConfig, WarpGrepResult, GitHubSearchToolConfig, GitHubReadFileInput, GitHubReadFileResult, GitHubReadFileToolConfig } from './types.js';

/**
 * Input schema for the warp grep tool
 */
const TOOL_PARAMETERS = {
  type: 'object',
  properties: {
    search_term: { type: 'string', description: 'Search problem statement that this subagent is supposed to research for' },
  },
  required: ['search_term'],
} as const;

/**
 * OpenAI-native warp grep tool definition
 * 
 * @example
 * ```typescript
 * import OpenAI from 'openai';
 * import { warpGrepTool, execute } from '@morphllm/morphsdk/tools/warp-grep/openai';
 * 
 * const client = new OpenAI();
 * const response = await client.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [warpGrepTool],
 *   messages: [{ role: 'user', content: 'Find authentication middleware' }]
 * });
 * 
 * // Execute the tool call
 * const result = await execute({ search_term: '...' }, { repoRoot: '.' });
 * ```
 */
export const warpGrepTool: ChatCompletionTool = {
  type: 'function',
  function: {
    name: WARP_GREP_TOOL_NAME,
    description: WARP_GREP_DESCRIPTION,
    parameters: TOOL_PARAMETERS,
  },
};

/**
 * Execute warp grep search
 * 
 * @param input - Tool input with search_term
 * @param config - Configuration with repoRoot and optional provider
 * @returns Search results
 */
export async function execute(
  input: { search_term: string } | string,
  config: WarpGrepToolConfig
): Promise<WarpGrepResult> {
  return executeToolCall(input, config);
}

// Re-export formatResult for convenience
export { formatResult };

/**
 * Create a custom warp grep tool with configuration and methods
 * 
 * @param config - Configuration options
 * @returns Tool definition with execute and formatResult methods
 * 
 * @example Local usage
 * ```typescript
 * import OpenAI from 'openai';
 * import { createWarpGrepTool } from '@morphllm/morphsdk/tools/warp-grep/openai';
 * 
 * const tool = createWarpGrepTool({ repoRoot: '.' });
 * 
 * const client = new OpenAI();
 * const response = await client.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [tool],
 *   messages: [{ role: 'user', content: 'Find authentication middleware' }]
 * });
 * ```
 * 
 * @example Remote sandbox (E2B, Modal, etc.)
 * ```typescript
 * const tool = createMorphWarpGrepTool({
 *   repoRoot: '/home/repo',
 *   remoteCommands: {
 *     grep: async (pattern, path) => (await sandbox.run(`rg '${pattern}' '${path}'`)).stdout,
 *     read: async (path, start, end) => (await sandbox.run(`sed -n '${start},${end}p' '${path}'`)).stdout,
 *     listDir: async (path, maxDepth) => (await sandbox.run(`find '${path}' -maxdepth ${maxDepth}`)).stdout,
 *   },
 * });
 * ```
 */
export function createWarpGrepTool(config: WarpGrepToolConfig) {
  const tool: ChatCompletionTool = {
    type: 'function',
    function: {
      name: config.name ?? WARP_GREP_TOOL_NAME,
      description: config.description ?? WARP_GREP_DESCRIPTION,
      parameters: TOOL_PARAMETERS,
    },
  };

  return Object.assign(tool, {
    execute: async (input: unknown): Promise<WarpGrepResult> => {
      return executeToolCall(input as { search_term: string } | string, config);
    },
    formatResult: (result: WarpGrepResult): string => {
      return formatResult(result);
    },
  });
}

/**
 * Create a GitHub search tool for OpenAI SDK
 *
 * @param config - Configuration options (morphApiKey, morphApiUrl, codeSearchUrl, timeout)
 * @returns OpenAI ChatCompletionTool with execute and formatResult methods
 *
 * @example
 * ```typescript
 * import OpenAI from 'openai';
 * import { createGitHubSearchTool } from '@morphllm/morphsdk/tools/warp-grep/openai';
 *
 * const tool = createGitHubSearchTool({ morphApiKey: process.env.MORPH_API_KEY });
 *
 * const response = await openai.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [tool],
 *   messages: [{ role: 'user', content: 'How does auth work in vercel/next.js?' }]
 * });
 *
 * // Execute
 * const result = await tool.execute({ search_term: 'auth middleware', github_url: 'https://github.com/vercel/next.js' });
 * console.log(tool.formatResult(result));
 * ```
 */
export function createGitHubSearchTool(config: GitHubSearchToolConfig) {
  const client = new WarpGrepClient(config);

  const tool: ChatCompletionTool = {
    type: 'function',
    function: {
      name: GITHUB_SEARCH_TOOL_NAME,
      description: GITHUB_SEARCH_DESCRIPTION,
      parameters: GITHUB_SEARCH_INPUT_SCHEMA,
    },
  };

  return Object.assign(tool, {
    execute: async (input: { search_term: string; github_url?: string; owner_repo?: string; branch?: string }): Promise<WarpGrepResult> => {
      const github = input.github_url || input.owner_repo;
      if (!github) {
        throw new Error('Please provide github search url or owner/repo id');
      }
      return client.searchGitHub({ searchTerm: input.search_term, github, branch: input.branch });
    },
    formatResult: (result: WarpGrepResult): string => {
      return formatResult(result);
    },
  });
}

/**
 * Create a GitHub read file tool for OpenAI SDK
 *
 * @param config - Optional configuration (timeout)
 * @returns OpenAI ChatCompletionTool with execute and formatResult methods
 *
 * @example
 * ```typescript
 * import OpenAI from 'openai';
 * import { createGitHubReadFileTool } from '@morphllm/morphsdk/tools/warp-grep/openai';
 *
 * const tool = createGitHubReadFileTool();
 *
 * const response = await openai.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [tool],
 *   messages: [{ role: 'user', content: 'Read the package.json from vercel/next.js' }]
 * });
 *
 * const result = await tool.execute({ github: 'vercel/next.js', path: 'package.json' });
 * console.log(tool.formatResult(result));
 * ```
 */
export function createGitHubReadFileTool(config?: GitHubReadFileToolConfig) {
  const tool: ChatCompletionTool = {
    type: 'function',
    function: {
      name: GITHUB_READ_FILE_TOOL_NAME,
      description: GITHUB_READ_FILE_DESCRIPTION,
      parameters: GITHUB_READ_FILE_INPUT_SCHEMA,
    },
  };

  return Object.assign(tool, {
    execute: async (input: GitHubReadFileInput): Promise<GitHubReadFileResult> => {
      return executeGitHubReadFile(input, config);
    },
    formatResult: (result: GitHubReadFileResult): string => {
      return formatGitHubReadFileResult(result);
    },
  });
}

export default warpGrepTool;
